// src/components/admin/design/index.tsx
"use client";

import React from "react";
import { Sun, Moon, Sparkles } from "lucide-react";
import HeroBannerPreview from "./HeroBannerPreview";
import CategoriesPreview from "./CategoriesPreview";
import DealsPreview from "./DealsPreview";
import FeaturedProductsPreview from "./FeaturedProductsPreview";
import AiRecommenderPreview from "./AiRecommenderPreview";
import BestSellersPreview from "./BestSellersPreview";
import NavbarPreview from "./NavbarPreview";
import WeeklyBannerPreview from "./WeeklyBannerPreview";
import BlogSectionRenderer from "@/components/store/sections/blog-posts";
import FaqSectionRenderer from "@/components/store/sections/faqs";
import TestimonialSectionRenderer from "@/components/store/sections/testimonials";
import GallerySectionRenderer from "@/components/store/sections/gallery";
import NewsletterSectionRenderer from "@/components/store/sections/newsletter";
import GenericFallbackPreview from "./GenericFallbackPreview";
import {
  useDetectedAdminTheme,
  DESIGN_STUDIO_MODAL_FIX_CSS,
} from "./UniversalProductCardPreview";

export interface SectionPreviewProps {
  sectionKey: string;
  variantId: string;
  primaryColor?: string;
  theme?: "dark" | "light";
  subdomain?: string;
}

const PRODUCT_CARD_SECTIONS = new Set([
  "ai_recommender",
  "deals_of_the_day",
  "featured_products",
  "best_sellers",
]);

export default function SectionPreviewRenderer({
  sectionKey,
  variantId,
  primaryColor = "#059669",
  theme: propTheme,
  subdomain,
}: SectionPreviewProps) {
  const [activeTheme, setActiveTheme] = useDetectedAdminTheme(propTheme);
  const isLight = activeTheme === "light";

  // Product card sections already include their own theme bar inside UniversalProductCardPreview
  if (PRODUCT_CARD_SECTIONS.has(sectionKey)) {
    switch (sectionKey) {
      case "ai_recommender":
        return (
          <AiRecommenderPreview
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
            subdomain={subdomain}
          />
        );
      case "deals_of_the_day":
        return (
          <DealsPreview
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
            subdomain={subdomain}
          />
        );
      case "featured_products":
        return (
          <FeaturedProductsPreview
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
            subdomain={subdomain}
          />
        );
      case "best_sellers":
        return (
          <BestSellersPreview
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
            subdomain={subdomain}
          />
        );
    }
  }

  const renderInnerSection = () => {
    switch (sectionKey) {
      case "navbar":
        return (
          <NavbarPreview
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
            subdomain={subdomain}
          />
        );
      case "hero_banner":
        return (
          <HeroBannerPreview
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
          />
        );
      case "categories":
        return (
          <CategoriesPreview
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
          />
        );
      case "wide_banner":
      case "weekly_banner":
        return (
          <WeeklyBannerPreview
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
            subdomain={subdomain}
          />
        );
      case "blog_posts":
        return <BlogSectionRenderer variant={variantId} primaryColor={primaryColor} />;
      case "faqs":
        return <FaqSectionRenderer variant={variantId} primaryColor={primaryColor} />;
      case "testimonials":
        return <TestimonialSectionRenderer variant={variantId} primaryColor={primaryColor} />;
      case "gallery":
        return <GallerySectionRenderer variant={variantId} primaryColor={primaryColor} />;
      case "newsletter":
        return (
          <NewsletterSectionRenderer
            variant={variantId}
            primaryColor={primaryColor}
            subdomain={subdomain}
          />
        );
      default:
        return (
          <GenericFallbackPreview
            sectionKey={sectionKey}
            variantId={variantId}
            primaryColor={primaryColor}
            theme={activeTheme}
          />
        );
    }
  };

  return (
    <div
      data-xylo-preview-theme={activeTheme}
      className={`w-full rounded-2xl border p-3.5 sm:p-4 space-y-3 overflow-hidden transition-colors ${
        isLight
          ? "bg-white border-slate-200/90 text-slate-900 shadow-xs"
          : "bg-[#0b1120] border-slate-800/90 text-slate-100 shadow-lg dark"
      }`}
    >
      <style dangerouslySetInnerHTML={{ __html: DESIGN_STUDIO_MODAL_FIX_CSS }} />

      {/* Top Preview Mode Bar (Light & Dark Mode Switcher) */}
      <div
        className={`flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b ${
          isLight ? "border-slate-200" : "border-slate-800"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            style={{
              color: primaryColor,
              borderColor: `${primaryColor}40`,
              backgroundColor: `${primaryColor}15`,
            }}
            className="px-2.5 py-1 rounded-full border text-[10px] font-black uppercase font-mono flex items-center gap-1.5 truncate"
          >
            <Sparkles className="w-3 h-3 shrink-0" />
            <span className="truncate">
              {sectionKey.replace(/_/g, " ")} — {variantId}
            </span>
          </span>
        </div>

        <div
          className={`inline-flex items-center p-0.5 rounded-xl border text-[10px] font-bold ${
            isLight
              ? "bg-slate-100 border-slate-200 text-slate-600"
              : "bg-slate-900 border-slate-800 text-slate-400"
          }`}
        >
          <button
            type="button"
            onClick={() => setActiveTheme("light")}
            className={`px-2.5 py-1 rounded-lg flex items-center gap-1 transition-all cursor-pointer ${
              isLight
                ? "bg-white text-amber-600 shadow-2xs font-black"
                : "hover:text-slate-200"
            }`}
            title="Preview in Light Mode"
          >
            <Sun className="w-3 h-3" />
            <span>Light Mode</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTheme("dark")}
            className={`px-2.5 py-1 rounded-lg flex items-center gap-1 transition-all cursor-pointer ${
              !isLight
                ? "bg-slate-800 text-emerald-400 shadow-2xs font-black"
                : "hover:text-slate-900"
            }`}
            title="Preview in Dark Mode"
          >
            <Moon className="w-3 h-3" />
            <span>Dark Mode</span>
          </button>
        </div>
      </div>

      {/* Rendered Section Preview Stage */}
      <div className="w-full overflow-x-auto">{renderInnerSection()}</div>
    </div>
  );
}